import { IconWrapper } from '@flexo/general';
import React, { useState, useEffect, useRef } from 'react';
import { Textfield } from '../textfield/textfield';

type CountsMaps = {
  siteCount: Map<string, number>;
  memberCount: Map<string, number>;
};

type ClassNames = {
  wrapper: string;
  header: string;
  headerActions: string;
  selectAll: string;
  search: string;
  searchIcon: string;
  list: string;
  listWrapper: string;
  row: string;
  rowChecked: string;
  cellName: string;
  cellMuted: string;
  cellCheck: string;
  footer: string;
};

export interface AssignCommunitiesTableProps {
  communities: Array<any>;
  counts: CountsMaps;
  selectedIds: Set<string>;
  isAllSelected: boolean;
  onToggleRow(id: string): void;
  onToggleAll(): void;
  /** Optional callback for shift+click range selection */
  onRangeToggle?(ids: string[], select: boolean): void;
  search: string;
  onSearchChange(value: string): void;
  labels: {
    title: string;
    selectAll: string;
    searchPlaceholder: string;
    selectedCountText: string;
  };
  classes: ClassNames;
}

export const AssignCommunitiesTable: React.FC<AssignCommunitiesTableProps> = ({
  communities,
  counts,
  selectedIds,
  isAllSelected,
  onToggleRow,
  onToggleAll,
  onRangeToggle,
  search,
  onSearchChange,
  labels,
  classes,
}) => {
  // Track the last clicked index for shift+click range selection
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  // Store community IDs to detect list changes
  const communityIdsRef = useRef<string>('');

  // Reset lastClickedIndex when the communities list changes
  useEffect(() => {
    const currentIds = communities.map((c) => c?.communityId).join(',');
    if (communityIdsRef.current !== currentIds) {
      communityIdsRef.current = currentIds;
      setLastClickedIndex(null);
    }
  }, [communities]);

  /**
   * Handle row click with shift+click support for range selection
   */
  const handleRowClick = (
    id: string,
    index: number,
    event: React.MouseEvent
  ) => {
    // Shift+click for range selection
    if (event.shiftKey && lastClickedIndex !== null && onRangeToggle) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      const idsInRange = communities
        .slice(start, end + 1)
        .map((c) => c?.communityId);
      // Determine whether to select or deselect based on the target element's current state
      const shouldSelect = !selectedIds.has(id);
      onRangeToggle(idsInRange, shouldSelect);
    } else {
      // Normal click: toggle single row and update lastClickedIndex
      onToggleRow(id);
      setLastClickedIndex(index);
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <span className={'heading3'}>{labels.title}</span>
        <div className={classes.headerActions}>
          <div onClick={onToggleAll} className={'select-all-icon'}>
            {isAllSelected ? (
              <IconWrapper iconName="check-encircled_outline_primary" />
            ) : (
              <IconWrapper iconName="circle" />
            )}
          </div>
          <label className={`${classes.selectAll} paragraph`}>
            {labels.selectAll}
          </label>
          <div className={classes.search}>
            <Textfield
              textfieldColor="white"
              variant="default"
              name="search-communities"
              value={search}
              placeholder={labels.searchPlaceholder}
              onChange={(e: any) => onSearchChange(e.target.value)}
              onPaste={(e) => false}
              onCopy={(e) => false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
            <div
              className={classes.searchIcon}
              onClick={() => {
                if (search) {
                  onSearchChange('');
                }
              }}
              style={{ cursor: search ? 'pointer' : 'default' }}
            >
              <IconWrapper iconName={search ? 'cross' : 'search'} />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.listWrapper}>
        <div className={classes.list}>
          {communities.map((c: any, index: number) => {
            const id = c?.communityId;
            const sitesCount = (c?.sitesCount ?? counts.siteCount.get(id)) || 0;
            const membersCount =
              (c?.membersCount ?? counts.memberCount.get(id)) || 0;
            const code =
              c?.communityCode ||
              c?.communityExternalId ||
              c?.communityId ||
              '';
            const checked = selectedIds.has(id);
            return (
              <div
                className={`${classes.row} ${
                  checked ? classes.rowChecked : ''
                }`}
                key={id}
                onClick={(e) => handleRowClick(id, index, e)}
              >
                <div className={`${classes.cellName} paragraph`}>
                  {c?.communityName || '-'}
                </div>
                <div className={`${classes.cellMuted} detail`}>{code}</div>
                <div className={`${classes.cellMuted} detail`}>
                  {sitesCount} Sites
                </div>
                <div className={`${classes.cellMuted} detail`}>
                  {membersCount} Members
                </div>
                <div className={classes.cellCheck}>
                  {checked ? (
                    <IconWrapper iconName="check-encircled_outline_primary" />
                  ) : (
                    <IconWrapper iconName="circle" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.footer}>
        <span className={'detail'}>{labels.selectedCountText}</span>
      </div>
    </div>
  );
};

export default AssignCommunitiesTable;
